// Copyright (c) Docugami, Inc. All rights reserved.

import { RegularButton } from "components/common/Fonts"
import { backgroundGray } from "components/shell/presentation/AppStyles"
import { useRef, useState } from "react"
import styled, { css } from "styled-components"
import { ContextualMenu, IContextualMenuItem } from "@fluentui/react";

const PickerContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-content: center;
    justify-content: center;
    gap: 20px;
`;

interface IPickerItem {
    id: string | null;
    icon: JSX.Element;
    name: string;
    isSelected: boolean;
    onSelected: () => void;
    commands?: IContextualMenuItem[];
}

interface SourcePickerProps {
    items: IPickerItem[];
    hidden?: boolean;
}

export const SourcePicker = (props: SourcePickerProps): JSX.Element => {
    return (
        <PickerContainer hidden={props.hidden}>
            {
                props.items.map((item, idx) => {
                    return <PickerItem key={`${item.id}_${idx}`} item={item} />
                })
            }
        </PickerContainer>
    )
}

const PickerItemContainer = styled.div<{ isSelected: boolean }>`
    display: flex;
    flex-direction: column;
    border-radius: 10px;
    width: 160px;
    cursor: pointer;
    ${props => props.isSelected && SelectedItemCss}
    :hover {
        box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.2);
        :active {
            background: ${backgroundGray};
        }
    }
`;

const SelectedItemCss = css`
    background: ${backgroundGray};
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.2);
`;

const ItemContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 23px;
    align-items: center;
    margin: 0 25px 25px 25px;
    text-align: center;
`

const IconContainer = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
`

const PickerItemTitle = styled(RegularButton)`
    white-space: nowrap;
`;

interface PickerItemProps {
    item: IPickerItem;
}

const PickerItem = (props: PickerItemProps): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const menuAnchorRef = useRef<HTMLDivElement>(null);
    const { id, name, icon, isSelected, onSelected, commands } = props.item;

    function onClick() {
        if (!!commands) {
            setIsOpen(v => !v);
        }
        onSelected();
    }

    return (
        <PickerItemContainer key={id} ref={menuAnchorRef} isSelected={isSelected} onClick={() => onClick()} >
            <ItemContentContainer>
                <IconContainer >
                    { icon }
                </IconContainer>
                <PickerItemTitle>{name.toUpperCase()}</PickerItemTitle>
            </ItemContentContainer>
            <ContextualMenu styles={{root: {right: '200px'} }} hidden={!isOpen} onDismiss={() => setIsOpen(false)} target={menuAnchorRef} items={commands ?? []} />
        </PickerItemContainer>
    );
}
